import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Link, MessagePlugin, Space, Switch, Tag } from 'tdesign-react';
import { useMemoizedFn } from 'ahooks';
import Descriptions from '@/components/Descriptions';
import { updateMetadata } from '@/services/topicService';
import { fetchPartition } from '@/slices/topicSlice';
import EllipsisTip from '@/components/EllipsisTip';
import { updateMessageTraceOpen } from '@/services/utilService';
import usePerms from '@/hooks/usePerms';
import DisableWrapper from '@/components/DisableWrapper';
import StatsModal from './StatsModal';
import StatsInternalModal from './StatsInternalModal';
import Producer from './Producer';
import Subscriptions from './Subscriptions';

function BasicPanel({ commonProps, overview, onRefresh, fetchPartition, data }) {
  const [visible, setVisible] = useState(false);
  const [visibleInternal, setVisibleInternal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasMessageTraceSwitch] = usePerms(['pulsar:topic:list:messageTraceSwitch']);

  console.log(commonProps);

  const onNoteChange = async (note) => {
    const params = {
      ...commonProps,
      note,
    };
    try {
      await updateMetadata(params);
      MessagePlugin.success('修改成功！');
      onRefresh();
    } catch (error) {
      return false;
    }
  };

  // 是否分区
  const isPartition = useMemo(() => overview.topicPartitionCount > 0, [overview]);

  const doInit = useMemoizedFn(() => {
    fetchPartition(commonProps);
  });

  useEffect(() => {
    doInit();
  }, [doInit]);

  const openChange = async (value) => {
    setSubmitting(true);
    try {
      await updateMessageTraceOpen({
        clusterName: commonProps.clusterName,
        resourceName: commonProps.topic,
        resourceType: 'topic',
        messageTraceOpen: value,
      });
      onRefresh();
    } catch (error) {
      console.trace(error);
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Root>
      <Descriptions title='基本信息' bordered>
        <Descriptions.Item label='Topic名称' value={overview.topic} />
        <Descriptions.Item label='命名空间' value={overview.namespace} />
        <Descriptions.Item label='所属租户' value={overview.tenant} />
        <Descriptions.Item
          label='Topic类型'
          value={
            <Space size='small'>
              <Tag theme='warning' size='small' variant='light'>
                {overview.topic?.startsWith('persistent') ? '持久化' : '非持久化'}
              </Tag>
              <Tag theme='warning' size='small' variant='light'>
                {overview.topicPartitionCount ? '分区' : '非分区'}
              </Tag>
            </Space>
          }
        />
        {isPartition && <Descriptions.Item label='分区数' value={overview.topicPartitionCount} />}

        <Descriptions.Item
          label='Topic状态'
          value={
            <Link theme='primary' onClick={() => setVisible(true)}>
              查看Stats
            </Link>
          }
        />
        <Descriptions.Item
          label='偏移量状态'
          value={
            <Link theme='primary' onClick={() => setVisibleInternal(true)}>
              查看Stats Internal
            </Link>
          }
        />
        <Descriptions.Item label='归属集群' value={overview.clusterName} />
        <Descriptions.Item
          label='消息轨迹'
          tip={overview.messageTraceNote}
          value={
            <DisableWrapper>
              <Switch
                label={['开启', '关闭']}
                loading={submitting}
                value={overview.messageTraceOpen}
                onChange={openChange}
                disabled={!hasMessageTraceSwitch}
              />
            </DisableWrapper>
          }
        />
        <Descriptions.Item label='创建时间' value={overview.createTime} type='datetime' />
        {overview.note && ( // 业务逻辑
          <Descriptions.Item
            label='备注'
            render={(val) => <EllipsisTip>{val}</EllipsisTip>}
            value={overview.note}
            editable
            onChange={onNoteChange}
          />
        )}
      </Descriptions>
      {!isPartition && (
        <>
          <Descriptions title='Bundle' bordered>
            {Object.entries(data.bundle || {}).map(([key, value]) => (
              <Descriptions.Item label={key} value={value} key={key} />
            ))}
          </Descriptions>
          <Descriptions.Panel title='生产者' bordered>
            <Producer data={data.statsProducers || []} />
          </Descriptions.Panel>
          <Descriptions.Panel title='订阅者' bordered>
            <Subscriptions data={data.statsSubscriptions || []} />
          </Descriptions.Panel>
        </>
      )}
      <StatsModal overview={overview} visible={visible} handleClose={() => setVisible(false)} />
      <StatsInternalModal
        overview={overview}
        visible={visibleInternal}
        handleClose={() => setVisibleInternal(false)}
      />
    </Root>
  );
}

export default connect(
  (state) => ({
    overview: state.topic.detailOverview,
    data: state.topic.partition,
  }),
  {
    fetchPartition,
  },
)(BasicPanel);

const Root = styled.div`
  padding: 0 6px;
  .descriptions {
    margin-top: 16px;
  }
`;
